import {
  endOfDay,
  getDate,
  getHours,
  getMilliseconds,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isSameDay,
  startOfDay,
} from 'date-fns';
import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendar/weekViewUtils';
import { Location } from '@atom/hooks/useCurrentLocation';
import { TaskTemplate } from '@atom/types/taskTemplate';
import {
  Summary,
  WorkOrderBulkCreateInput,
  WorkOrderBulkField,
  WorkOrderFullCreateInput,
  WorkOrderFullCreateTaskInput,
  WorkOrderType,
} from '@atom/types/work';
import {
  addMillisecondToDate,
  convertDateToMillisGMTMidday,
  convertMinutesToMilliseconds,
  roundUpByMilliseconds,
} from '@atom/utilities/timeUtilities';
import { stripTypename } from '@atom/utilities/typenameUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  getFieldTreeIds,
  getInvalidFieldId,
  getRootFieldById,
  isRequiredOnCreateFieldInvalid,
} from '@atom/utilities/workOrderFieldUtilities';

import {
  DraftTask,
  ModalStep,
  ModalStepType,
  WorkCreateType,
  WorkOrderDraft,
  WorkTemplateSelection,
} from './CreateWorkModalContext';

export const MAX_BULK_QUANTITY = 100;

const SIXTY_MINUTES_IN_MILLISECONDS = convertMinutesToMilliseconds(60);
const FIFTEEN_MINUTES_IN_MILLISECONDS = convertMinutesToMilliseconds(15);

export type AssetRequirement = {
  showAssetAndLocation: boolean;
  requireAsset: boolean;
};

export const DEFAULT_TASK_NAME = 'Untitled Task';
export const DEFAULT_SUMMARY_TITLE = 'Untitled Summary';

// Restores the pre-edit title on blur when the user has cleared it
export const restoreSummaryTitleOnBlur = (
  summary: Summary[],
  index: number,
  fallbackTitle: string,
): Summary[] =>
  summary.map((item, itemIndex) =>
    itemIndex === index && isNilOrEmpty(item.title?.trim())
      ? {
          ...item,
          title: isNilOrEmpty(fallbackTitle?.trim())
            ? DEFAULT_SUMMARY_TITLE
            : fallbackTitle,
        }
      : item,
  );

// Prefills each level of the cascade revealed by a single select's value with
// that level's defaultValue. Levels that already have a value are kept.
export const applyCascadeDefaultValues = (
  field: WorkOrderBulkField,
): WorkOrderBulkField =>
  isNilOrEmpty(field.subFields) || isNilOrEmpty(field.value)
    ? field
    : {
        ...field,
        subFields: field.subFields.map(subField =>
          subField.matchValue === field.value
            ? {
                ...subField,
                nestedField: applyCascadeDefaultValues({
                  ...subField.nestedField,
                  value: isNilOrEmpty(subField.nestedField.value)
                    ? subField.nestedField.defaultValue
                    : subField.nestedField.value,
                }),
              }
            : subField,
        ),
      };

const updateFieldValue = (
  field: WorkOrderBulkField,
  fieldId: string,
  value: any,
): WorkOrderBulkField => {
  if (field.id === fieldId) {
    return applyCascadeDefaultValues({ ...field, value });
  }

  if (isNilOrEmpty(field.subFields)) {
    return field;
  }

  return {
    ...field,
    subFields: field.subFields.map(subField => ({
      ...subField,
      nestedField: updateFieldValue(subField.nestedField, fieldId, value),
    })),
  };
};

export const updateFieldValueById = (
  fields: WorkOrderBulkField[],
  fieldId: string,
  value: any,
): WorkOrderBulkField[] =>
  fields.map(field => updateFieldValue(field, fieldId, value));

// Ids of required-on-create fields missing a value. For a cascading single
// select, this is the level of the selected cascade still missing a value.
export const getRequiredOnCreateInvalidFieldIds = (
  fields: WorkOrderBulkField[],
): string[] =>
  fields.filter(isRequiredOnCreateFieldInvalid).map(getInvalidFieldId);

// Clears any error on the (possibly cascading) field containing fieldId.
// Errors are only raised when leaving the details step, so newly revealed
// cascade levels are not flagged until the user tries to advance again.
export const clearRequiredFieldIds = (
  requiredFieldIds: Set<string>,
  fieldId: string,
  fields: WorkOrderBulkField[],
): Set<string> => {
  const rootField = getRootFieldById(fieldId, fields);

  if (!rootField) {
    return requiredFieldIds;
  }

  const treeIds = new Set(getFieldTreeIds(rootField));
  const otherFieldIds = [...requiredFieldIds].filter(id => !treeIds.has(id));

  return otherFieldIds.length === requiredFieldIds.size
    ? requiredFieldIds
    : new Set(otherFieldIds);
};

export const MODAL_STEPS: { [key in ModalStep]: ModalStepType } = {
  [ModalStep.TEMPLATE]: {
    value: ModalStep.TEMPLATE,
    title: 'Work Template',
  },
  [ModalStep.DETAILS]: {
    value: ModalStep.DETAILS,
    title: 'Work Details',
  },
  [ModalStep.TASKS]: {
    value: ModalStep.TASKS,
    title: 'Tasks',
  },
};

export const getModalStepsArray = (
  mode: WorkCreateType = 'SINGLE',
  disableTaskCreation = false,
): ModalStepType[] =>
  (mode === 'BULK' || disableTaskCreation
    ? [ModalStep.TEMPLATE, ModalStep.DETAILS]
    : [ModalStep.TEMPLATE, ModalStep.DETAILS, ModalStep.TASKS]
  ).map(step => MODAL_STEPS[step]);

export const draftTaskToInput = (
  task: DraftTask,
): WorkOrderFullCreateTaskInput =>
  task.taskTemplateId
    ? { taskTemplateId: task.taskTemplateId }
    : { name: task.name };

export const canRenameTask = (task: DraftTask): boolean =>
  !!task.createdInModal && isNilOrEmpty(task.taskTemplateId);

export const canDeleteTask = (task: DraftTask): boolean =>
  !!task.createdInModal;

export const addTemplateTask = (
  tasks: DraftTask[],
  taskTemplate: TaskTemplate,
): DraftTask[] => [
  {
    id: uuid(),
    name: taskTemplate.name,
    taskTemplateId: taskTemplate.id,
    users: taskTemplate.users || [],
    userGroups: taskTemplate.userGroups || [],
    createdInModal: true,
  } as DraftTask,
  ...tasks,
];

export const addBlankTask = (tasks: DraftTask[]): DraftTask[] => [
  { id: uuid(), name: DEFAULT_TASK_NAME, createdInModal: true } as DraftTask,
  ...tasks,
];

export const renameTask = (
  tasks: DraftTask[],
  id: string,
  name: string,
): DraftTask[] =>
  tasks.map(task => (task.id === id ? { ...task, name } : task));

export const removeTask = (tasks: DraftTask[], id: string): DraftTask[] =>
  tasks.filter(task => task.id !== id);

export const getTasksForAssetChange = (
  draft: WorkOrderDraft,
  nextAssetId: string | null,
): DraftTask[] =>
  isNilOrEmpty(draft.workTemplate) && draft.assetId !== nextAssetId
    ? []
    : draft.tasks || [];

export const sanitizeFieldForInput = (
  field: WorkOrderBulkField,
): WorkOrderBulkField => {
  const clean = stripTypename(R.omit(['subFieldsTruncated'], field));

  return isNilOrEmpty(clean.subFields)
    ? clean
    : {
        ...clean,
        subFields: clean.subFields.map(subField => ({
          ...stripTypename(subField),
          nestedField: sanitizeFieldForInput(subField.nestedField),
        })),
      };
};

export const workOrderDraftToFullCreateInput = (
  draft: WorkOrderDraft,
  pointLocation: Location | null = null,
): WorkOrderFullCreateInput => {
  const tasks = (draft.tasks || [])
    .filter(task => task.createdInModal)
    .map(draftTaskToInput);

  const hasAsset = !!(draft.assetId || draft.asset?.id);
  const location =
    !hasAsset && draft.isLocationBased ? pointLocation : draft.location;

  const input = {
    workTemplateId: draft.workTemplate?.id,
    name: draft.name,
    assetId: draft.assetId || draft.asset?.id,
    dueDate: draft.dueDate ? draft.dueDate.valueOf() : null,
    plannedStartDate: draft.plannedStartDate
      ? draft.plannedStartDate.valueOf()
      : null,
    location,
    summary: draft.summary,
    fields: (draft.fields || []).map(sanitizeFieldForInput),
    tasks,
  };

  return R.reject(isNilOrEmpty, input) as WorkOrderFullCreateInput;
};

export const isBulkQuantityValid = (quantity: number): boolean =>
  Number.isInteger(quantity) && quantity > 0 && quantity <= MAX_BULK_QUANTITY;

export const workOrderDraftToBulkCreateInput = (
  draft: WorkOrderDraft,
  pointLocation: Location | null,
): WorkOrderBulkCreateInput => {
  const location = draft.isLocationBased ? pointLocation : null;

  const input = {
    quantity: draft.quantity,
    location,
    workTemplateId: draft.workTemplate?.id,
    dueDate: draft.dueDate ? convertDateToMillisGMTMidday(draft.dueDate) : null,
  };

  return R.reject(isNilOrEmpty, input) as WorkOrderBulkCreateInput;
};

export const getAssetRequirement = (
  workTemplate: WorkTemplateSelection,
): AssetRequirement => {
  const workTemplateType = R.pathOr<WorkOrderType>(
    WorkOrderType.DEFAULT,
    ['type'],
    workTemplate,
  );

  const showAssetAndLocation = workTemplateType === WorkOrderType.DEFAULT;

  return {
    showAssetAndLocation,
    requireAsset: showAssetAndLocation && !!workTemplate?.schemaId,
  };
};

export type CreateTaskGuardView = 'template' | 'blank';

export const getNoTaskGuardCreateView = (
  requireTaskType: boolean,
  hasTaskTemplates: boolean,
): CreateTaskGuardView =>
  requireTaskType || hasTaskTemplates ? 'template' : 'blank';

export type StartAndDueDateSeed = {
  fromCalendarDate: Date;
  fromCalendarGap: CalendarGap;
};

const dayAtCurrentTime = (day: Date): Date => {
  const targetDate = new Date(day);
  const currentTime = new Date();

  return new Date(
    getYear(targetDate),
    getMonth(targetDate),
    getDate(targetDate),
    getHours(currentTime),
    getMinutes(currentTime),
    getSeconds(currentTime),
    getMilliseconds(currentTime),
  );
};

export const getStartAndDueDateInitialValues = ({
  fromCalendarDate,
  fromCalendarGap,
}: StartAndDueDateSeed): {
  plannedStartDate: Date;
  dueDate: Date;
} => {
  if (!!fromCalendarGap) {
    // use this block for gap-based initialization
    const roundedStartDate = roundUpByMilliseconds(
      new Date(fromCalendarGap.plannedStartDate),
      FIFTEEN_MINUTES_IN_MILLISECONDS,
    );
    const roundedDueDate = roundUpByMilliseconds(
      new Date(fromCalendarGap.dueDate),
      FIFTEEN_MINUTES_IN_MILLISECONDS,
    );

    const getFirstGapValues = () => {
      // plannedStartDate is an hour before gap dueDate for first gap
      // - if roundedDueDate within first hour of day, set plannedStartDate to start of day
      const calculatedStartTime = addMillisecondToDate(
        roundedDueDate,
        -SIXTY_MINUTES_IN_MILLISECONDS,
      );
      const startIsDifferentDay = !isSameDay(
        calculatedStartTime,
        roundedDueDate,
      );
      return {
        plannedStartDate: startIsDifferentDay
          ? startOfDay(roundedDueDate)
          : calculatedStartTime,
        // dueDate is same as end of first gap
        dueDate: roundedDueDate,
      };
    };

    const getGapValues = () => {
      // plannedStartDate is same as start of gap for remaining gaps
      // - if roundedStartDate within last hour of day, set dueDate to end of day
      const calculatedEndTime = addMillisecondToDate(
        roundedStartDate,
        SIXTY_MINUTES_IN_MILLISECONDS,
      );
      const endIsDifferentDay = !isSameDay(calculatedEndTime, roundedStartDate);
      return {
        plannedStartDate: roundedStartDate,
        // dueDate is an hour after gap plannedStartDate for remaining gaps
        dueDate: endIsDifferentDay
          ? endOfDay(roundedStartDate)
          : calculatedEndTime,
      };
    };

    return fromCalendarGap.firstGap ? getFirstGapValues() : getGapValues();
  }

  // Round up current date to closest 15 minute
  const roundedDate = roundUpByMilliseconds(
    fromCalendarDate ? dayAtCurrentTime(fromCalendarDate) : new Date(),
    FIFTEEN_MINUTES_IN_MILLISECONDS,
  );

  // Start date is current rounded date
  const plannedStartDate = roundedDate;

  // Due date is current rounded date + 60 minutes
  const dueDate = addMillisecondToDate(
    roundedDate,
    SIXTY_MINUTES_IN_MILLISECONDS,
  );

  // If true, the dates are on the same day
  const datesAreSameDay = isSameDay(plannedStartDate, dueDate);

  return {
    plannedStartDate,
    // If dueDate is not same day as plannedStartDate, then do not populate
    dueDate: datesAreSameDay ? dueDate : null,
  };
};

export const isStepDisabled = (
  step: ModalStep,
  draft: WorkOrderDraft,
  createDisabled: boolean,
): boolean => {
  switch (step) {
    case ModalStep.TEMPLATE:
      return isNilOrEmpty(draft.workTemplate) && !draft.startedFromScratch;
    case ModalStep.DETAILS: {
      if (draft.mode === 'BULK') {
        return !isBulkQuantityValid(draft.quantity);
      }

      if (isNilOrEmpty(draft.name)) {
        return true;
      }

      const appointmentsEnabled =
        draft.workTemplate?.appointmentsEnabled ?? false;

      if (
        appointmentsEnabled &&
        (R.isNil(draft.plannedStartDate) ||
          R.isNil(draft.dueDate) ||
          draft.plannedStartDate.valueOf() > draft.dueDate.valueOf())
      ) {
        return true;
      }

      const { requireAsset } = getAssetRequirement(draft.workTemplate);

      return requireAsset && isNilOrEmpty(draft.assetId) && !draft.asset;
    }
    case ModalStep.TASKS:
      return createDisabled;
    default:
      return false;
  }
};
